import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  getPaymentsByCompany,
  getMultiYearSummary,
  formatCurrency,
  checkExclusions,
  checkDualInterestBatch,
  checkOwnershipBatch,
  checkResearchBatch,
  getOwnershipByNPI,
  getDualInterestByNPI,
  getOwnershipDetailByNPI,
  getResearchByNPI,
  getResearchDetailByNPI,
  getResearchGeneralDualInterestByNPI,
  getResearchOwnershipDualInterest,
  getDirectResearchByNPI,
} from "../utils/cmsApi";
import { DATASET_YEARS } from "../utils/cmsApi";

const ALL_YEARS = Object.keys(DATASET_YEARS).map(Number).sort();

const CHART_COLORS = ["#f59e0b", "#6366f1", "#14b8a6"];

const TABS = [
  { id: "general", label: "General" },
  { id: "ownership", label: "Ownership" },
  { id: "research", label: "Research" },
];

function buildProviders(npis, names, states, totals) {
  return npis.map((npi, i) => ({
    npi,
    name: names[i] || "Unknown",
    state: states[i] || "",
    totalAllYears: parseFloat(totals[i]) || 0, // used as fallback only
  }));
}

function ProviderColumn({ provider, year, colorIndex, conflicts }) {
  const [companies, setCompanies] = useState([]);
  const [exclusions, setExclusions] = useState({ oig: [], sam: [] });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const color = CHART_COLORS[colorIndex];

  useEffect(() => {
    setLoading(true);
    getPaymentsByCompany(provider.npi, year)
      .then(setCompanies)
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));

    const nameParts = provider.name.split(" ");
    checkExclusions(provider.npi, nameParts[0], nameParts[nameParts.length - 1], provider.state)
      .then((data) => setExclusions({ oig: data.oig || [], sam: data.sam || [] }))
      .catch(() => {});
  }, [provider.npi, year]);

  const totalYear = companies.reduce((s, c) => s + c.total, 0);
  const topCompany = companies[0];
  const topPct = totalYear > 0 && topCompany ? ((topCompany.total / totalYear) * 100).toFixed(1) : 0;
  const hasExclusions = exclusions.oig.length > 0 || exclusions.sam.length > 0;

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          {/* Colored marker */}
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <Link
            to={`/physician/${provider.npi}?name=${encodeURIComponent(provider.name)}&state=${provider.state}&totalAllYears=${provider.totalAllYears}`}
            className="font-display text-xl text-white hover:text-gold-300 transition-colors block"
          >
            {provider.name}
          </Link>
        </div>
        <p className="text-white/30 text-xs font-mono mt-0.5 ml-5">NPI: {provider.npi}</p>
        <button
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/physician/${provider.npi}?name=${encodeURIComponent(provider.name)}&state=${provider.state}&totalAllYears=${provider.totalAllYears}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors mt-1 ml-5"
        >
          {copied ? "✓ Link copied!" : "⎘ Share profile"}
        </button>

        </div>

      {/* Exclusion flags — always first */}
      {hasExclusions && (
        <div className="space-y-1">
          {exclusions.oig.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
              <p>⚠ OIG Excluded</p>
              {exclusions.oig[0].matchType === "name_state" && (
                <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
              )}
            </div>
          )}
          {exclusions.sam.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-400">
              <p>⚠ SAM.gov Excluded</p>
              {exclusions.sam[0].matchType === "name_state" && (
                <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Conflict flags — below exclusions, consistent with ownership/research tabs */}
      {conflicts && (conflicts.hasTripleInterest || conflicts.hasPaymentOwnership || conflicts.hasResearchOwnership || conflicts.hasResearchGeneral) && (
        <div className="space-y-1">
          {conflicts.hasTripleInterest ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
              ⚠ Triple Interest Conflict
            </div>
          ) : (
            <>
              {conflicts.hasPaymentOwnership && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-400">
                  ⚑ General & Ownership Conflict
                </div>
              )}
              {conflicts.hasResearchOwnership && (
                <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg px-3 py-2 text-xs text-teal-400">
                  ⚗ Ownership & Research Conflict
                </div>
              )}
              {conflicts.hasResearchGeneral && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 text-xs text-blue-400">
                  ⚗ General & Research Conflict
                </div>
              )}
            </>
          )}
        </div>
      )}      

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg" style={{ color }}>{formatCurrency(provider.totalAllYears)}</div>
          <div className="text-white/30 text-xs mt-0.5">all years</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="text-white font-display text-lg">{formatCurrency(totalYear)}</div>
          <div className="text-white/30 text-xs mt-0.5">{year || "current year"}</div>
        </div>
      </div>

{/* Top 3 companies */}
      {topCompany && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Top companies ({year})</p>
          <div className="bg-navy-700/50 rounded-lg p-3 space-y-3">
            {companies.slice(0, 3).map((company, idx) => {
              const pct = totalYear > 0 ? ((company.total / totalYear) * 100).toFixed(1) : 0;
              return (
                <div key={company.companyName}>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-white/80 text-xs font-medium truncate max-w-32">{company.companyName}</p>
                    <span className="text-xs shrink-0 ml-2" style={{ color }}>{formatCurrency(company.total)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white/5 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: color,
                          opacity: 1 - idx * 0.25,
                        }}
                      />
                    </div>
                    <span className="text-white/30 text-xs shrink-0">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}      

      {/* Payment categories */}
      {!loading && companies.length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Payment types ({year})</p>
          <div className="space-y-1">
            {Object.entries(
              companies.reduce((acc, c) => {
                Object.entries(c.categories).forEach(([cat, amt]) => {
                  acc[cat] = (acc[cat] || 0) + amt;
                });
                return acc;
              }, {})
            )
              .sort((a, b) => b[1] - a[1])
              .slice(0, 4)
              .map(([cat, amt]) => (
                <div key={cat} className="flex justify-between items-center">
                  <span className="text-white/50 text-xs truncate max-w-32">{cat}</span>
                  <span className="text-white/40 text-xs font-mono">{formatCurrency(amt)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Companies count + year range */}
      <div className="border-t border-white/5 pt-3 space-y-1">
        <p className="text-white/30 text-xs">
          {companies.length} {companies.length === 1 ? "company" : "companies"} in {year}
        </p>
        {provider.firstYear && provider.lastYear && (
          <p className="text-white/20 text-xs">
            {provider.firstYear === provider.lastYear
              ? `${provider.firstYear}`
              : `${provider.firstYear}–${provider.lastYear} · ${provider.lastYear - provider.firstYear + 1} years`}
          </p>
        )}
        <Link
          to={`/physician/${provider.npi}?name=${encodeURIComponent(provider.name)}&state=${provider.state}&totalAllYears=${provider.totalAllYears}`}
          className="print:hidden text-xs transition-colors hover:opacity-80 block mt-1"
          style={{ color }}
        >
          View full profile →
        </Link>
      </div>
    </div>
  );
}

function formatCompact(value) {
  if (!value || value === 0) return "—";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function categorizeInterest(terms) {
  if (!terms) return "Other";
  const t = terms.toUpperCase();
  if (t.includes("COMMON") && t.includes("PREFERRED")) return "Common & Preferred Stock";
  if (t.includes("RESTRICTED STOCK") || t.includes("RSU") || t.includes("PRSU") || t.includes("DEFERRED STOCK UNIT")) return "Restricted Stock / RSUs";
  if (t.includes("STOCK OPTION") || t.includes("OPTION TO PURCHASE") || t.includes("STOCK AND OR OPTIONS") || t.includes("STOCK AND/OR OPTIONS") || t.includes("OPTION GRANT") || t.includes("OPTIONS GRANTED") || t.includes("OPTIONS EXERCISED") || t.includes("EXERCISED OPTION") || t.includes("OPTIONS FOR SERVICES") || t.includes("OPTION FOR COMMON") || t.includes("NSO GRANT") || t.includes("VESTED OPTION") || t.includes("UNEXERCISED OPTION") || t.includes("BLACK-SCHOLES") || t.includes("BLACK SCHOLES") || t.includes("ADVISOR OPTION") || (t.includes("OPTION") && t.includes("EXERCISE"))) return "Stock Options";
  if (t.includes("WARRANT")) return "Warrants";
  if (t.includes("COMMON STOCK") || t.includes("COMMON SHARE") || t.includes("COMMON UNIT") || t.includes("ORDINARY SHARE") || t.includes("FOUNDER") || t === "COMMON" || t.includes("SHARES OF STOCK") || t.includes("COMPANY STOCK") || t.includes("STOCK OWNERSHIP") || t.includes("STOCK HOLDER") || t.includes("STOCKHOLDER") || t.includes("SHARE OWNERSHIP") || t.includes("STOCK GRANT") || t.includes("STOCK PURCHASE") || t.includes("PURCHASED CORPORATE SHARE") || t.includes("EQUITY STOCK") || t.includes("SHARES OF THE COMPANY") || t.includes("SHARES OF COMPANY") || t.includes("STOCK INTEREST")) return "Common Stock";
  if (t.includes("PREFERRED") || t.includes("SERIES A") || t.includes("SERIES B") || t.includes("SERIES C") || t.includes("SERIES D") || t.includes("SERIES E") || t.includes("SERIES F") || t.includes("SERIES SEED")) return "Preferred Stock";
  if (t.includes("LLC") || t.includes("L.L.C") || t.includes("MEMBERSHIP INTEREST") || t.includes("MEMBERSHIP UNIT") || t.includes("PROFIT INTEREST") || t.includes("PROFITS INTEREST") || t.includes("OPERATING AGREEMENT") || t.includes("CLASS A UNIT") || t.includes("CLASS B UNIT") || t.includes("CLASS C UNIT") || t.includes("CLASS D UNIT") || t.includes("B UNITS") || t.includes("A UNITS") || t.includes("UNIT HOLDER") || t.includes("UNITHOLDER")) return "LLC / Membership Interest";
  if (t.includes("CONVERTIBLE") || t.includes("SAFE INVESTMENT") || t.includes("SAFE 20%") || t.includes("PROMISSORY NOTE") || t === "SAFE") return "Convertible Instruments";
  if (t.includes("PARTNERSHIP") || t.includes("LP ") || t.includes("LLP") || t.includes("CAPITAL CONTRIBUTION") || t.includes("CAPITAL INVESTMENT") || t.includes("PARTNER CAPITAL") || t.includes("PARTNERSHIP SHARES")) return "Partnership Interest";
  if (t.includes("ACQUISITION") || t.includes("LIQUIDAT") || t.includes("REPURCHAS") || t.includes("BOUGHT BACK") || t.includes("BUYOUT") || t.includes("MERGER") || t.includes("COMPANY SOLD") || t.includes("ASSETS SOLD") || t.includes("CEASED BUSINESS") || t.includes("DISSOLVED") || t.includes("BANKRUPTCY") || t.includes("OUT OF BUSINESS")) return "Acquisition / Liquidation";
  if (t.includes("ROYALT") || t.includes("DIVIDEND") || t.includes("DISTRIBUTION") || t.includes("10% OF NET PROFIT") || t.includes("PROFIT BASED ON") || t.includes("20% RETURN") || t.includes("20% PROFIT")) return "Royalties / Distributions";
  if (t === "EQUITY" || t === "INVESTMENT" || t === "STOCK" || t === "SHARES" || t === "OWNERSHIP" || t === "INVESTOR" || t.includes("EQUITY INVESTMENT") || t.includes("EQUITY INTEREST") || t.includes("EQUITY OWNERSHIP") || t === "STOCK OWNERSHIP" || t === "PURCHASED SHARES" || t.includes("CASH INVESTMENT") || t.includes("PRIVATE PLACEMENT") || t.includes("OWNERSHIP INTEREST")) return "Equity Investment";
  if (!t || t === "NONE" || t === "N/A" || t === "NA" || t.trim() === "") return "Other";
  return "Other";
}

function OwnershipColumn({ provider, color, loaded }) {
  const [ownership, setOwnership] = useState(null);
  const [dualInterest, setDualInterest] = useState(null);
  const [researchOwnershipDual, setResearchOwnershipDual] = useState([]);
  const [detail, setDetail] = useState(null);
  const [exclusions, setExclusions] = useState({ oig: [], sam: [] });
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!loaded || fetched) return;
    setLoading(true);
    setFetched(true);
    const nameParts = provider.name.split(" ");
    Promise.all([
      getOwnershipByNPI(provider.npi).catch(() => []),
      getDualInterestByNPI(provider.npi).catch(() => []),
      getOwnershipDetailByNPI(provider.npi).catch(() => []),
      checkExclusions(provider.npi, nameParts[0], nameParts[nameParts.length - 1], provider.state).catch(() => ({ oig: [], sam: [] })),
      getResearchOwnershipDualInterest(provider.npi).catch(() => []),
    ]).then(([own, dual, det, excl, resOwnDual]) => {
      setOwnership(own);
      setDualInterest(dual);
      setDetail(det);
      setExclusions({ oig: excl.oig || [], sam: excl.sam || [] });
      setResearchOwnershipDual(resOwnDual || []);
      setLoading(false);
    });
  }, [loaded, provider.npi, fetched]);

  if (!loaded) return null;

  if (loading) {
    return (
      <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
        <div className="text-white/30 text-sm animate-pulse">Loading ownership data…</div>
      </div>
    );
  }

  if (!ownership || ownership.length === 0) {
    return (
      <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <span className="font-display text-xl text-white">{provider.name}</span>
        </div>
        {exclusions.oig.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
            <p>⚠ OIG Excluded</p>
            {exclusions.oig[0].matchType === "name_state" && (
              <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
            )}
          </div>
        )}
        {exclusions.sam.length > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-400">
            <p>⚠ SAM.gov Excluded</p>
            {exclusions.sam[0].matchType === "name_state" && (
              <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
            )}
          </div>
        )}
        <p className="text-white/30 text-sm">No ownership interest records found.</p>
      </div>
    );
  }

  const activeOwnership = ownership.filter((o) => o.hasActiveOwnership);
  const totalValue = activeOwnership.reduce((s, o) => s + (o.valueOfInterest || 0), 0);
  const activeCount = activeOwnership.length;
  const historicalCount = ownership.length - activeCount;

  // Interest type breakdown — active records only
  const interestTypeMap = {};
  activeOwnership.forEach((o) => {
    const t = categorizeInterest(o.termsOfInterest);
    if (!interestTypeMap[t]) interestTypeMap[t] = { count: 0, value: 0 };
    interestTypeMap[t].count += 1;
    interestTypeMap[t].value += o.valueOfInterest || 0;
  });
  // Remove "Other" if there are meaningful categories to show
  const meaningfulTypes = Object.entries(interestTypeMap).filter(([t]) => t !== "Other");
  const displayTypes = meaningfulTypes.length > 0
    ? Object.fromEntries(meaningfulTypes)
    : interestTypeMap;

  // Family member holder flag — only show if latest-year records include family involvement
  const latestYear = ownership.reduce((max, o) => Math.max(max, o.latestYear || 0), 0);
  const latestYearOwnership = ownership.filter((o) => o.latestYear === latestYear);
  const hasFamilyHolder = latestYearOwnership.some(
    (o) => o.interestHolder === "Immediate Family Member" ||
           o.interestHolder === "Physician and Immediate Family Member"
  );
  const hasJointHolder = latestYearOwnership.some(
    (o) => o.interestHolder === "Physician and Immediate Family Member"
  );

  // Dual interest flags — getDualInterestByNPI returns an array, not a single object
  const dualArr = Array.isArray(dualInterest) ? dualInterest : [];
  const hasGeneralDual = dualArr.length > 0;
  const hasResearchDual = (detail || []).length > 0 && false; // research dual handled separately

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <Link
            to={`/physician/${provider.npi}/ownership?name=${encodeURIComponent(provider.name)}&state=${provider.state}`}
            className="font-display text-xl text-white hover:text-gold-300 transition-colors"
          >
            {provider.name}
          </Link>
        </div>
        <p className="text-white/30 text-xs font-mono mt-0.5 ml-5">NPI: {provider.npi}</p>
      </div>

      {/* Exclusion banners */}
      {exclusions.oig.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
          <p>⚠ OIG Excluded</p>
          {exclusions.oig[0].matchType === "name_state" && (
            <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
          )}
        </div>
      )}
      {exclusions.sam.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-400">
          <p>⚠ SAM.gov Excluded</p>
          {exclusions.sam[0].matchType === "name_state" && (
            <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
          )}
        </div>
      )}

      {/* Conflict banners — titles and colors match PhysicianOwnersPage OwnersConflictBanners */}
      {ownership.length > 0 && (() => {
        // Deduplicate researchOwnershipDual by companyId before triple interest check
        const uniqueResOwnership = [...new Map(researchOwnershipDual.map((r) => [r.companyId, r])).values()];
        const tripleCount = dualArr.filter((d) =>
          uniqueResOwnership.some((r) => r.companyId === d.companyId)
        ).length;
        if (tripleCount > 0) return (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
            ⚠ Triple Interest Conflict
          </div>
        );
        return (
          <div className="space-y-1">
            {hasGeneralDual && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-400">
                ⚑ General & Ownership Conflict
              </div>
            )}
            {researchOwnershipDual.length > 0 && (
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg px-3 py-2 text-xs text-teal-400">
                ⚗ Ownership & Research Conflict
              </div>
            )}
          </div>
        );
      })()}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="text-green-400 font-display text-lg">{activeCount}</div>
          <div className="text-white/30 text-xs mt-0.5">active</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="text-white/50 font-display text-lg">{historicalCount}</div>
          <div className="text-white/30 text-xs mt-0.5">historical</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg" style={{ color }}>
            {totalValue > 0 ? formatCompact(totalValue) : "—"}
          </div>
          <div className="text-white/30 text-xs mt-0.5">active value</div>
        </div>
      </div>

      {/* Family holder badge — only when family involvement exists in latest year */}
      {hasFamilyHolder && (
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg px-3 py-2 text-xs text-violet-300">
          ⚠ {hasJointHolder ? "Physician & immediate family member" : "Immediate family member"} holds interest in latest year
        </div>
      )}

      {/* Top companies by value */}
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Top companies</p>
        <div className="bg-navy-700/50 rounded-lg p-3 space-y-3">
            {(activeOwnership.length > 0 ? activeOwnership : ownership).slice(0, 3).map((o, idx) => {
              const pct = totalValue > 0
                ? ((o.valueOfInterest / totalValue) * 100).toFixed(1)
                : 0;
              return (
                <div key={o.companyName || idx}>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-white/80 text-xs font-medium truncate max-w-32">{o.companyName || "Unknown"}</p>
                    <span className="text-xs shrink-0 ml-2" style={{ color }}>
                      {o.valueOfInterest > 0 ? formatCompact(o.valueOfInterest) : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white/5 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{ width: `${pct}%`, background: color, opacity: 1 - idx * 0.25 }}
                      />
                    </div>
                    <span className="text-white/30 text-xs shrink-0">{pct}%</span>
                  </div>
                  <p className="text-green-400/70 text-xs mt-0.5">Active</p>
                </div>
              );
            })}
          </div>
      </div>

      {/* Interest type breakdown */}
      {Object.keys(displayTypes).length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Interest types</p>
          <div className="space-y-1.5">
            {Object.entries(displayTypes)
              .sort((a, b) => b[1].value - a[1].value)
              .slice(0, 4)
              .map(([type, { count, value }]) => (
                <div key={type}>
                  <div className="flex justify-between items-center">
                    <span className="text-white/50 text-xs truncate max-w-36">{type}</span>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      {value > 0 && (
                        <span className="text-white/60 text-xs font-mono">{formatCompact(value)}</span>
                      )}
                      <span className="text-white/25 text-xs">{count} co.</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="print:hidden border-t border-white/5 pt-3">
        <Link
          to={`/physician/${provider.npi}/ownership?name=${encodeURIComponent(provider.name)}&state=${provider.state}`}
          className="text-xs transition-colors hover:opacity-80"
          style={{ color }}
        >
          View full ownership profile →
        </Link>
      </div>
    </div>
  );
}

function ResearchColumn({ provider, color, loaded }) {
  const [research, setResearch] = useState(null);
  const [detail, setDetail] = useState(null);
  const [researchOwnershipDual, setResearchOwnershipDual] = useState([]);
  const [researchGeneralDual, setResearchGeneralDual] = useState([]);
  const [directResearch, setDirectResearch] = useState([]);
  const [exclusions, setExclusions] = useState({ oig: [], sam: [] });
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!loaded || fetched) return;
    setLoading(true);
    setFetched(true);
    const nameParts = provider.name.split(" ");
    Promise.all([
      getResearchByNPI(provider.npi).catch(() => []),
      getResearchDetailByNPI(provider.npi).catch(() => []),
      getResearchOwnershipDualInterest(provider.npi).catch(() => []),
      getResearchGeneralDualInterestByNPI(provider.npi).catch(() => []),
      checkExclusions(provider.npi, nameParts[0], nameParts[nameParts.length - 1], provider.state).catch(() => ({ oig: [], sam: [] })),
      getDirectResearchByNPI(provider.npi).catch(() => []),
    ]).then(([res, det, resDual, resGenDual, excl, direct]) => {
      setResearch(res);
      setDetail(det);
      setResearchOwnershipDual(resDual || []);
      setResearchGeneralDual(resGenDual || []);
      setDirectResearch(direct || []);
      setExclusions({ oig: excl.oig || [], sam: excl.sam || [] });
      setLoading(false);
    });
  }, [loaded, provider.npi, fetched]);

  if (!loaded) return null;

  if (loading) {
    return (
      <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
        <div className="text-white/30 text-sm animate-pulse">Loading research data…</div>
      </div>
    );
  }

  if (!research || research.length === 0) {
    return (
      <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <span className="font-display text-xl text-white">{provider.name}</span>
        </div>
        {exclusions.oig.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
            <p>⚠ OIG Excluded</p>
            {exclusions.oig[0].matchType === "name_state" && (
              <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
            )}
          </div>
        )}
        {exclusions.sam.length > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-400">
            <p>⚠ SAM.gov Excluded</p>
            {exclusions.sam[0].matchType === "name_state" && (
              <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
            )}
          </div>
        )}
        <p className="text-white/30 text-sm">No research payment records found.</p>
      </div>
    );
  }

  const totalFunding = research.reduce((s, r) => s + (r.totalAmount || 0), 0);
  const activeCount = research.filter((r) => r.hasActiveResearch).length;
  const companyCount = research.length;

  // Use hasPreclinical flag on summary rows — more reliable than string matching detail rows
  const preclinicalCompanies = research.filter((r) => r.hasPreclinical);
  const preclinicalFunding = preclinicalCompanies.reduce((s, r) => s + (r.totalAmount || 0), 0);
  const clinicalFunding = totalFunding - preclinicalFunding;
  const preclinicalPct = totalFunding > 0 ? ((preclinicalFunding / totalFunding) * 100).toFixed(0) : 0;
  const clinicalPct = totalFunding > 0 ? ((clinicalFunding / totalFunding) * 100).toFixed(0) : 0;
  const hasPreclinical = preclinicalCompanies.length > 0;

  // Research context breakdown from summary rows
  const contextMap = {};
  research.forEach((r) => {
    const ctx = r.primaryContext || "Other";
    contextMap[ctx] = (contextMap[ctx] || 0) + (r.totalAmount || 0);
  });
  const contextEntries = Object.entries(contextMap)
    .filter(([ctx]) => ctx !== "Other" && ctx !== "")
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <Link
            to={`/physician/${provider.npi}/research?name=${encodeURIComponent(provider.name)}&state=${provider.state}`}
            className="font-display text-xl text-white hover:text-gold-300 transition-colors"
          >
            {provider.name}
          </Link>
        </div>
        <p className="text-white/30 text-xs font-mono mt-0.5 ml-5">NPI: {provider.npi}</p>
      </div>

      {/* Exclusion banners */}
      {exclusions.oig.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
          <p>⚠ OIG Excluded</p>
          {exclusions.oig[0].matchType === "name_state" && (
            <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
          )}
        </div>
      )}
      {exclusions.sam.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-400">
          <p>⚠ SAM.gov Excluded</p>
          {exclusions.sam[0].matchType === "name_state" && (
            <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
          )}
        </div>
      )}

      {/* Conflict banners — titles and colors match PhysicianResearchPage ResearchConflictBanners */}
      {research && research.length > 0 && (() => {
        // Deduplicate by companyId — tables now have pi_based + direct rows per company
        const uniqueResOwn = [...new Map(researchOwnershipDual.map((d) => [d.companyId, d])).values()];
        const uniqueResGen = [...new Map(researchGeneralDual.map((g) => [g.companyId, g])).values()];
        const resOwn = uniqueResOwn.length;
        const resGen = uniqueResGen.length;
        const tripleCount = uniqueResOwn.filter((d) =>
          uniqueResGen.some((g) => g.companyId === d.companyId)
        ).length;
        if (tripleCount > 0) return (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
            ⚠ Triple Interest Conflict
          </div>
        );
        return (
          <div className="space-y-1">
            {resOwn > 0 && (
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg px-3 py-2 text-xs text-teal-400">
                ⚗ Ownership & Research Conflict
              </div>
            )}
            {resGen > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 text-xs text-blue-400">
                ⚗ General & Research Conflict
              </div>
            )}
          </div>
        );
      })()}

      {/* Stats */}
      {(() => {
        // Latest year from PI-based detail data
        const latestYear = (detail || []).length > 0
          ? Math.max(...(detail || []).map((d) => d.programYear))
          : null;
        const latestYearAssociated = latestYear
          ? (detail || []).filter((d) => d.programYear === latestYear).reduce((s, d) => s + d.totalAmount, 0)
          : 0;

        // Direct research totals
        const totalDirect = directResearch.reduce((s, d) => s + d.totalAmount, 0);
        const hasDirectResearch = totalDirect > 0;
        const latestYearDirect = latestYear
          ? directResearch.filter((d) => d.year === latestYear).reduce((s, d) => s + d.totalAmount, 0)
          : 0;
        const latestYearDirectCount = latestYear
          ? directResearch.filter((d) => d.year === latestYear).reduce((s, d) => s + d.paymentCount, 0)
          : 0;

        return (
          <div className="space-y-2">
            {/* Row 1 — Associated research funding */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-navy-700/50 rounded-lg p-3 text-center">
                <div className="font-display text-lg" style={{ color }}>{formatCurrency(totalFunding)}</div>
                <div className="text-white/30 text-xs mt-0.5">total funding</div>
              </div>
              <div className="bg-navy-700/50 rounded-lg p-3 text-center">
                <div className="font-display text-lg" style={{ color }}>{formatCurrency(latestYearAssociated)}</div>
                <div className="text-white/30 text-xs mt-0.5">{latestYear ? `${latestYear} funding` : "—"}</div>
              </div>
            </div>

            {/* Row 2 — Direct research payments (only if exists) */}
            {hasDirectResearch && (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-amber-500/[0.05] border border-amber-500/15 rounded-lg p-3 text-center">
                  <div className="text-amber-400 font-display text-lg">{formatCurrency(totalDirect)}</div>
                  <div className="text-white/30 text-xs mt-0.5">direct payments</div>
                </div>
                <div className="bg-amber-500/[0.05] border border-amber-500/15 rounded-lg p-3 text-center">
                  <div className="text-amber-400 font-display text-lg">{formatCurrency(latestYearDirect)}</div>
                  <div className="text-white/30 text-xs mt-0.5">
                    {latestYear ? `${latestYear} direct` : "—"}
                    {latestYearDirectCount > 0 && ` · ${latestYearDirectCount} pmts`}
                  </div>
                </div>
              </div>
            )}

            {/* Active count — only if relevant */}
            {activeCount > 0 && (
              <div className="bg-navy-700/50 rounded-lg p-3 text-center">
                <div className="text-green-400 font-display text-lg">{activeCount}</div>
                <div className="text-white/30 text-xs mt-0.5">active (2025)</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Top companies by funding */}
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Top companies</p>
        <div className="bg-navy-700/50 rounded-lg p-3 space-y-3">
          {research.slice(0, 3).map((r, idx) => {
            const pct = totalFunding > 0 ? ((r.totalAmount / totalFunding) * 100).toFixed(1) : 0;
            return (
              <div key={r.companyName || idx}>
                <div className="flex justify-between items-center mb-1">
                  <p className="text-white/80 text-xs font-medium truncate max-w-32">{r.companyName || "Unknown"}</p>
                  <span className="text-xs shrink-0 ml-2" style={{ color }}>{formatCurrency(r.totalAmount)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white/5 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${pct}%`, background: color, opacity: 1 - idx * 0.25 }}
                    />
                  </div>
                  <span className="text-white/30 text-xs shrink-0">{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preclinical split */}
      {hasPreclinical && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Preclinical vs clinical</p>
          <div className="bg-navy-700/50 rounded-lg p-3 space-y-2">
            {/* Split bar */}
            <div className="flex rounded-full overflow-hidden h-2">
              <div className="bg-teal-500/70" style={{ width: `${clinicalPct}%` }} />
              <div className="bg-violet-500/70" style={{ width: `${preclinicalPct}%` }} />
            </div>
            <div className="flex justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-sm bg-teal-500/70 shrink-0" />
                <span className="text-white/50">Clinical</span>
                <span className="text-white/30">{clinicalPct}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-sm bg-violet-500/70 shrink-0" />
                <span className="text-white/50">Preclinical</span>
                <span className="text-violet-400">{preclinicalPct}%</span>
              </div>
            </div>
            <div className="flex justify-between text-xs pt-1 border-t border-white/5">
              <span className="text-white/30">{formatCurrency(clinicalFunding)}</span>
              <span className="text-violet-400/70">{formatCurrency(preclinicalFunding)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Research context breakdown */}
      {contextEntries.length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Research context</p>
          <div className="space-y-1">
            {contextEntries.map(([ctx, amt]) => (
              <div key={ctx} className="flex justify-between items-center">
                <span className="text-white/50 text-xs truncate max-w-36">{ctx}</span>
                <span className="text-white/40 text-xs font-mono">{formatCurrency(amt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="print:hidden border-t border-white/5 pt-3">
        <Link
          to={`/physician/${provider.npi}/research?name=${encodeURIComponent(provider.name)}&state=${provider.state}`}
          className="text-xs transition-colors hover:opacity-80"
          style={{ color }}
        >
          View full research profile →
        </Link>
      </div>
    </div>
  );
}

function MultiYearCompareChart({ providers, activeTab }) {
  const [generalData, setGeneralData] = useState({});
  const [ownershipData, setOwnershipData] = useState({});
  const [researchData, setResearchData] = useState({});

  // Fetch general payments on mount
  useEffect(() => {
    providers.forEach((p) => {
      getMultiYearSummary(p.npi)
        .then((data) => {
          const byYear = {};
          ALL_YEARS.forEach((y) => { byYear[y] = 0; });
          data.forEach((row) => { byYear[row.year] = (byYear[row.year] || 0) + row.totalPayments; });
          setGeneralData((prev) => ({ ...prev, [p.npi]: byYear }));
        })
        .catch(() => {});
    });
  }, [providers.map(p => p.npi).join(",")]);

  // Fetch ownership data when ownership tab first activated
  useEffect(() => {
    if (activeTab !== "ownership") return;
    providers.forEach((p) => {
      if (ownershipData[p.npi]) return; // already fetched
      getOwnershipDetailByNPI(p.npi)
        .then((detail) => {
          const byYear = {};
          ALL_YEARS.forEach((y) => { byYear[y] = 0; });
          (detail || []).forEach((row) => {
            const yr = row.programYear || row.year;
            if (yr) byYear[yr] = (byYear[yr] || 0) + (row.valueOfInterest || 0);
          });
          setOwnershipData((prev) => ({ ...prev, [p.npi]: byYear }));
        })
        .catch(() => {});
    });
  }, [activeTab, providers.map(p => p.npi).join(",")]);

  // Fetch research data when research tab first activated
  useEffect(() => {
    if (activeTab !== "research") return;
    providers.forEach((p) => {
      if (researchData[p.npi]) return; // already fetched
      getResearchDetailByNPI(p.npi)
        .then((detail) => {
          const byYear = {};
          ALL_YEARS.forEach((y) => { byYear[y] = 0; });
          (detail || []).forEach((row) => {
            const yr = parseInt(row.year) || parseInt(row.programYear);
            if (yr) byYear[yr] = (byYear[yr] || 0) + (row.totalPayments || row.totalAmount || 0);
          });
          setResearchData((prev) => ({ ...prev, [p.npi]: byYear }));
        })
        .catch(() => {});
    });
  }, [activeTab, providers.map(p => p.npi).join(",")]);

  const chartData = activeTab === "ownership" ? ownershipData
    : activeTab === "research" ? researchData
    : generalData;

  const chartTitle = activeTab === "ownership" ? "Ownership value trend — all years"
    : activeTab === "research" ? "Research funding trend — all years"
    : "Payment history — all years";

  const maxVal = Math.max(
    ...providers.flatMap((p) =>
      ALL_YEARS.map((y) => chartData[p.npi]?.[y] || 0)
    ), 1
  );

  return (
    <div className="card mb-6">
      <h2 className="text-white/40 text-xs uppercase tracking-widest mb-4">{chartTitle}</h2>
      <div className="flex gap-4 mb-3">
        {providers.map((p, i) => (
          <div key={p.npi} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ background: CHART_COLORS[i] }} />
            <span className="text-white/50 text-xs">{p.name}</span>
          </div>
        ))}
      </div>
      <div className="relative" style={{ height: "200px" }}>
        <div className="absolute inset-0 flex items-end gap-2 pb-6">
          {ALL_YEARS.map((year) => (
            <div key={year} className="flex-1 flex items-end gap-0.5 h-full relative group">
              {providers.map((p, i) => {
                const val = chartData[p.npi]?.[year] || 0;
                const heightPct = (val / maxVal) * 100;
                return (
                  <div key={p.npi} className="flex-1 flex flex-col items-center justify-end h-full relative">
                    {/* Data label */}
                    {val > 0 && (
                      <div
                        className="absolute w-full text-center pointer-events-none"
                        style={{ bottom: `${Math.max(heightPct, 2)}%`, paddingBottom: "2px" }}
                      >
                        <span style={{ fontSize: "8px", color: CHART_COLORS[i], opacity: 0.9 }}>
                          {val >= 1000000
                            ? `$${(val / 1000000).toFixed(1)}M`
                            : val >= 1000
                            ? `$${(val / 1000).toFixed(0)}K`
                            : `$${val.toFixed(0)}`}
                        </span>
                      </div>
                    )}
                    {/* Bar */}
                    <div
                      className="w-full rounded-t transition-all"
                      style={{
                        height: val > 0 ? `${Math.max(heightPct, 2)}%` : "2px",
                        background: val > 0 ? CHART_COLORS[i] : "rgba(255,255,255,0.05)",
                        opacity: 0.8,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex gap-1">
          {ALL_YEARS.map((year) => (
            <div key={year} className="flex-1 text-center">
              <span className="text-white/30" style={{ fontSize: "9px" }}>{year}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ComparePage({ year }) {
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [loadedTabs, setLoadedTabs] = useState({ general: true, ownership: false, research: false });

  const npis = (searchParams.get("npis") || "").split(",").filter(Boolean);
  const names = (searchParams.get("names") || "").split(",").map(decodeURIComponent);
  const states = (searchParams.get("states") || "").split(",");
  const totals = (searchParams.get("totals") || "").split(",");

  const providers = buildProviders(npis, names, states, totals);

  // Year range + all-years total — derived from multi-year summary
  // This is the authoritative source for both values, overriding URL params
  // which may be 0 if the provider was added from the Watch List
  const [yearRanges, setYearRanges] = useState({});
  const [allYearTotals, setAllYearTotals] = useState({});

  useEffect(() => {
    providers.forEach((p) => {
      getMultiYearSummary(p.npi)
        .then((data) => {
          const activeRows = (data || []).filter((r) => r.totalPayments > 0);
          const activeYears = activeRows.map((r) => r.year);
          const total = activeRows.reduce((s, r) => s + r.totalPayments, 0);
          if (activeYears.length > 0) {
            setYearRanges((prev) => ({
              ...prev,
              [p.npi]: { firstYear: Math.min(...activeYears), lastYear: Math.max(...activeYears) },
            }));
          }
          if (total > 0) {
            setAllYearTotals((prev) => ({ ...prev, [p.npi]: total }));
          }
        })
        .catch(() => {});
    });
  }, [npis.join(",")]);

  // Batch conflict checks for all compared providers
  const [conflictFlags, setConflictFlags] = useState({});

  useEffect(() => {
    if (providers.length === 0) return;
    const allNpis = providers.map((p) => p.npi).filter(Boolean);
    if (!allNpis.length) return;

    Promise.all([
      checkDualInterestBatch(allNpis).catch(() => []),
      checkOwnershipBatch(allNpis).catch(() => []),
      checkResearchBatch(allNpis).catch(() => []),
    ]).then(([dualResults, ownershipNpis, researchNpis]) => {
      const flags = {};
      const ownSet = new Set(ownershipNpis);
      const resSet = new Set(researchNpis);
      for (const npi of allNpis) {
        const dual = dualResults.find((d) => d.npi === npi);
        flags[npi] = {
          hasOwnership: ownSet.has(npi),
          hasResearch: resSet.has(npi),
          hasTripleInterest: dual?.hasTripleInterest || false,
          hasDualInterest: !!dual,
          hasPaymentOwnership: dual?.hasPaymentOwnership || false,
          hasResearchOwnership: dual?.hasResearchOwnership || false,
          hasResearchGeneral: dual?.hasResearchGeneral || false,
        };
      }
      setConflictFlags(flags);
    });
  }, [npis.join(",")]);

  if (providers.length === 0) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-10">
        <p className="text-white/40 text-sm">No providers selected for comparison.</p>
        <Link to="/" className="text-gold-400 text-sm mt-2 block">← Back to search</Link>
      </main>
    );
  }

  function shareComparison() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleTabClick(tabId) {
    setActiveTab(tabId);
    setLoadedTabs((prev) => ({ ...prev, [tabId]: true }));
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: ${providers.length === 3 ? "letter landscape" : "letter"}; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, header, footer, .print-hide { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card {
            background: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            break-inside: avoid;
          }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-gold-400 { color: #b45309 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }
          .text-violet-400 { color: #7c3aed !important; }
          svg text { fill: #374151 !important; }

          /* Force bar colors */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; opacity: 1 !important; }
          div[style*="rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="rgba(255,255,255,0.05"] { display: none !important; }

          /* Banner backgrounds */
          .bg-red-500\\/10 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-blue-500\\/10 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          .bg-violet-500\\/10 { background: #f5f3ff !important; border-color: #c4b5fd !important; }

          /* Stats backgrounds */
          .bg-navy-700\\/50 { background: #f9fafb !important; border: 1px solid #e5e7eb !important; }

          /* Column grid — force side-by-side in print */
          .compare-grid {
            display: grid !important;
            grid-template-columns: repeat(${providers.length}, 1fr) !important;
            gap: 12px !important;
          }

          /* Hide interactive controls */
          select { display: none !important; }
          button { display: none !important; }
          a[class*="hover"] { pointer-events: none !important; }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
              ProviderPayWatch · Provider Comparison Report
            </p>
            <h1 className="text-2xl font-bold text-gray-900">
              {providers.map((p) => p.name).join(" vs. ")}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {providers.length} providers · {year} data · {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
        </div>

      {/* Page header */}
      <div className="print:hidden flex items-center justify-between mb-6">
        <div>
          <Link to={-1} className="text-white/40 hover:text-white/70 text-sm transition-colors">
            ← Results
          </Link>
          <h1 className="font-display text-3xl text-white mt-2">
            Provider <span className="text-gold-400">comparison</span>
          </h1>
          <p className="text-white/40 text-sm mt-1">
            Comparing {providers.length} providers · {year} data
          </p>
          <p className="text-white/20 text-xs mt-0.5">
            Data covers 2016–2025 · totals may differ from CMS site which shows recent years only
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={shareComparison}
            className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-all ${
              copied
                ? "text-green-400 border-green-500/30 bg-green-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            {copied ? "✓ Link copied!" : "⎘ Share comparison"}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
          >
            <span>⎙</span><span>Print / Save PDF</span>
          </button>
        </div>
      </div>

      {/* Multi-year chart — always visible, switches data based on active tab */}
      <div className="overflow-x-auto -mx-4 px-4">
        <div style={{ minWidth: providers.length > 1 ? "500px" : "auto" }}>
          <MultiYearCompareChart providers={providers} activeTab={activeTab} />
        </div>
      </div>

      {/* Tab bar */}
      <div className="print:hidden flex gap-1 mb-5 border-b border-white/10">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? "text-gold-400 border-gold-400"
                : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab hint for ownership/research */}
      {activeTab !== "general" && (
        <p className="print:hidden text-white/20 text-xs mb-4">
          {activeTab === "ownership"
            ? "Ownership & investment interest data from CMS Open Payments"
            : "Research payment data from CMS Open Payments"}
        </p>
      )}

      {/* General tab */}
      {activeTab === "general" && (
        <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
          <div
            className="grid gap-4 compare-grid"
            style={{
              gridTemplateColumns: `repeat(${providers.length}, minmax(280px, 1fr))`,
              minWidth: providers.length > 1 ? `${providers.length * 290}px` : "auto",
            }}
          >
            {providers.map((provider, i) => (
              <ProviderColumn
                key={provider.npi}
                provider={{
                  ...provider,
                  ...yearRanges[provider.npi],
                  totalAllYears: allYearTotals[provider.npi] ?? provider.totalAllYears,
                }}
                year={year}
                colorIndex={i}
                conflicts={conflictFlags[provider.npi]}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ownership tab — columns mount but only fetch when loadedTabs.ownership is true */}
      {activeTab === "ownership" && (
        <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
          <div
            className="grid gap-4 compare-grid"
            style={{
              gridTemplateColumns: `repeat(${providers.length}, minmax(280px, 1fr))`,
              minWidth: providers.length > 1 ? `${providers.length * 290}px` : "auto",
            }}
          >
            {providers.map((provider, i) => (
              <OwnershipColumn
                key={provider.npi}
                provider={provider}
                color={CHART_COLORS[i]}
                loaded={loadedTabs.ownership}
              />
            ))}
          </div>
        </div>
      )}

      {/* Research tab */}
      {activeTab === "research" && (
        <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
          <div
            className="grid gap-4 compare-grid"
            style={{
              gridTemplateColumns: `repeat(${providers.length}, minmax(280px, 1fr))`,
              minWidth: providers.length > 1 ? `${providers.length * 290}px` : "auto",
            }}
          >
            {providers.map((provider, i) => (
              <ResearchColumn
                key={provider.npi}
                provider={provider}
                color={CHART_COLORS[i]}
                loaded={loadedTabs.research}
              />
            ))}
          </div>
        </div>
      )}

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments · Data reported by manufacturers under the Sunshine Act
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8">
        Source: CMS Open Payments (2016–2025) · Data reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}